// Lib
import { compact, isEmpty, first } from 'lodash';

// Utils
import logger from '../../utils/logger';
import { isAlias, isBoard } from '../../../common/elements/utils/elementTypeUtils';
import { getPhysicalAncestors } from '../../../common/elements/utils/elementTraversalUtils';
import { hasReadPermission } from '../../../common/permissions/elementPermissionsUtil';
import {
    getElementId,
    getLinkedElementId,
    getLocationParentId,
} from '../../../common/elements/utils/elementPropertyUtils';
import { isLocationTrash } from '../../../common/elements/utils/elementLocationUtils';

const MAX_BREADCRUMB_ITERATIONS = 1000;

const getAliasIds = (elements, linkedElementId) =>
    elements
        .filter(
            (element) =>
                isAlias(element) && getLinkedElementId(element) === linkedElementId && !isLocationTrash(element),
        )
        .map((alias) => getElementId(alias))
        .toArray();

// Find 'adjacent node' IDs.  I.e. The parent ID and alias IDs
export const findParentIds = (elements, elementId) => {
    const element = elements.get(elementId);

    if (!element) {
        logger.info('No element in BFS', elementId);
        return [];
    }

    return compact([getLocationParentId(element), ...getAliasIds(elements, elementId)]);
};

// Returns all the elements that we're expecting to have, but we don't currently have
export const getMissingElementIds = (elements, elementIds) =>
    elementIds.filter((elementId) => !elements.get(elementId));

const getPath = (previousNodes, finalElementId) => {
    const visitedNodes = {};
    const path = [];
    let currentElementId = finalElementId;

    while (currentElementId) {
        path.push(currentElementId);
        currentElementId = previousNodes[currentElementId];

        if (visitedNodes[currentElementId]) {
            console.warn(`Infinite loop in breadcrumbUtil/getPath for: ${finalElementId}`);
            break;
        }
        visitedNodes[currentElementId] = true;
    }
    return path;
};

/**
 * This is the path to the highest element that can be viewed by the specified user.
 */
const getReadablePath = ({ currentBoardId, elements, aclIds }) =>
    getPhysicalAncestors(elements, currentBoardId)
        .filter((ancestor) => hasReadPermission(elements, getElementId(ancestor), aclIds))
        .map((ancestor) => getElementId(ancestor))
        .reverse();

/**
 * Remove columns and skeletons from the breadcrumb list
 */
const filterOutColumnsAndSkeletons = (elements) => (elementId) => {
    const element = elements.get(elementId);
    return isBoard(element);
};

const getBreadthFirstBreadcrumb = ({ currentBoardId, rootBoardId, elements, aclIds }) => {
    if (!currentBoardId) return {};
    // Perform a breadth first search of elements until we find the root element or we encounter a missing element
    // Start at the current board and work backwards
    let elementId = currentBoardId;
    let queue = [elementId];
    let missingElementIds = [];
    let iterationCount = 0;

    const visitedElementIds = {};
    const previousNodes = { [elementId]: null };

    // Function to add an ID to the previous nodes map
    const addToPreviousNodes = (id) => {
        previousNodes[id] = previousNodes[id] || elementId;
    };

    // Guest users won't have a rootBoardId so they will skip this and go straight to
    // the public board calculation
    while (rootBoardId && queue.length && iterationCount < MAX_BREADCRUMB_ITERATIONS) {
        iterationCount++;

        // Get the front of the queue
        elementId = queue.shift();

        // If we've already visited this element then don't check it again, otherwise there will be an infinite loop
        if (visitedElementIds[elementId]) continue;

        visitedElementIds[elementId] = true;

        // We've found what we need
        if (elementId === rootBoardId) {
            const fullPath = getPath(previousNodes, elementId);
            return { complete: true, path: fullPath.filter(filterOutColumnsAndSkeletons(elements)), fullPath };
        }

        // Otherwise check all 'adjacent nodes' (parents or aliases)
        const parentIds = findParentIds(elements, elementId);
        parentIds
            // Only use the unvisited ones
            .filter((parentId) => !visitedElementIds[parentId])
            .forEach(addToPreviousNodes);
        queue = queue.concat(parentIds);

        // Keep track of any missing element IDs in case we don't find a full path, we can fetch them
        missingElementIds = missingElementIds.concat(getMissingElementIds(elements, parentIds));
    }

    if (iterationCount >= MAX_BREADCRUMB_ITERATIONS) {
        logger.error('Max breadcrumb iterations reached');
    }

    // We weren't able to find the target
    // If we have missing elements then we need to resolve them before being sure that we're 'complete'
    if (missingElementIds.length) {
        return { complete: false, missingElementIds };
    }

    // If the root couldn't be found and there aren't any missing element IDs then find the shared path
    // I.e. The path at which the user has permission to view the board (if one exists).
    const sharedPath = getReadablePath({ currentBoardId, elements, aclIds });

    // This must be because the user is viewing a shared board but currently has not entered the password
    if (!sharedPath.length) {
        // NOTE: Might want to make sure that the current board exists in the state?

        // So there's currently no path to root, and there's currently no shared path
        // Just return the currentBoardId as the breadcrumb and render it in the detached state
        return {
            complete: true,
            detached: true,
            protected: true,
            path: [currentBoardId],
            fullPath: [currentBoardId],
        };
    }

    return {
        complete: true,
        detached: true,
        path: sharedPath.filter(filterOutColumnsAndSkeletons(elements)),
        fullPath: sharedPath,
    };
};

/**
 * This handles the two most common breadcrumb scenarios:
 *  - The current board is within the user's physical tree
 *  - The board is within a shared board, and the user has a single alias to the shared board.
 *
 * If one of these scenarios is not true, this function won't return a breadcrumb.
 */
const getPhysicalOrSingleSharedBoardBreadcrumb = ({ currentBoardId, rootBoardId, elements, aclIds }) => {
    const readablePath = getReadablePath({ currentBoardId, elements, aclIds });

    if (isEmpty(readablePath)) return;

    const readableRootId = first(readablePath);

    if (readableRootId === rootBoardId) {
        return {
            complete: true,
            detached: false,
            path: readablePath.filter(filterOutColumnsAndSkeletons(elements)),
            fullPath: readablePath,
        };
    }

    // Otherwise, get the alias ID of the readable root ID and then get its physical path.
    // This case would satisfy 90% of breadcrumb scenarios
    const aliasIds = getAliasIds(elements, readableRootId);

    // If we don't have any aliases for this board, or we have multiple aliases, then bail
    // and fallback to the breadth first search
    if (!aliasIds || aliasIds.length !== 1) return;

    const aliasId = first(aliasIds);

    // Get the readable path
    const aliasReadablePath = getReadablePath({ currentBoardId: aliasId, elements, aclIds });

    const aliasReadableRootId = first(aliasReadablePath);

    // We didn't find a path back to root with one alias hop from the readable root
    if (aliasReadableRootId !== rootBoardId) return;

    const fullPath = [...aliasReadablePath, ...readablePath];

    return {
        complete: true,
        detached: false,
        path: fullPath.filter(filterOutColumnsAndSkeletons(elements)),
        fullPath,
    };
};

/**
 * Retrieves the breadcrumb to use for the current board.
 */
export const getBreadcrumb = ({ currentBoardId, rootBoardId, elements, aclIds }) =>
    // First try to get the physical / one hop breadcrumb as this scenario is the most common
    getPhysicalOrSingleSharedBoardBreadcrumb({ currentBoardId, rootBoardId, elements, aclIds }) ||
    // Otherwise, fallback to the old method, which performs a breadth first search
    getBreadthFirstBreadcrumb({ currentBoardId, rootBoardId, elements, aclIds });
